import React from "react";
import { UseCase, UseCaseStep, StepActionParameter } from "../../types/TestTarget";
import { getActionConfig } from "../../utils/actionConfig";
import { uuidv7 } from "uuidv7";
import { StepEditor } from "./StepEditor";

interface BaseStepsListProps {
  editedUseCase: UseCase;
  setEditedUseCase: React.Dispatch<React.SetStateAction<UseCase | null>>;
  policyIndex: number;
  policy: any;
  localTestTargets: any[];
  currentViewId?: string;
  onRemovePolicyStep: (
    policyIndex: number,
    changeType: "add_steps" | "update_steps" | "remove_steps",
    stepIndex: number
  ) => void;
  onUpdatePolicyStep: (
    policyIndex: number,
    changeType: "add_steps" | "update_steps" | "remove_steps",
    stepIndex: number,
    field: string,
    value: string
  ) => void;
  onCreateTestTarget?: (callback?: (newTarget: any) => void) => void;
  policyStepValidationErrors?: Map<string, string>;
}

export const BaseStepsList: React.FC<BaseStepsListProps> = ({
  editedUseCase,
  setEditedUseCase,
  policyIndex,
  policy,
  localTestTargets,
  currentViewId,
  onRemovePolicyStep,
  onUpdatePolicyStep,
  onCreateTestTarget,
  policyStepValidationErrors = new Map(),
}) => {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: "6px",
        padding: "16px",
        background: "#fafbfc",
        marginBottom: "16px",
      }}
    >
      <h5
        style={{
          margin: "0 0 16px 0",
          fontSize: "0.9rem",
          fontWeight: "600",
          color: "#1e293b",
        }}
      >
        ベースユースケースの変更
      </h5>

      <div>
        <h6
          style={{
            margin: "0 0 12px 0",
            fontSize: "0.8rem",
            fontWeight: "600",
            color: "#4b5563",
          }}
        >
          ベースユースケース - ステップ ({editedUseCase.steps.length})
        </h6>
        <div
          style={{
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            background: "white",
            maxHeight: "350px",
            overflowY: "auto",
          }}
        >
          {/* 最初の位置に追加ボタンまたは入力フィールド */}
          {(() => {
            const insertPosition = "start";
            const addedStep = policy.changes.add_steps.find(
              (s: any) => s.insert_position === insertPosition
            );

            if (addedStep) {
              const stepIndex = policy.changes.add_steps.findIndex(
                (s: any) => s.insert_position === insertPosition
              );
              return (
                <div
                  style={{
                    padding: "12px",
                    borderBottom: "1px solid #f3f4f6",
                    background: "#f0fdf4",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: "500",
                      color: "#059669",
                      marginBottom: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>+ 先頭に新規ステップ追加</span>
                    <button
                      type="button"
                      onClick={() => onRemovePolicyStep(policyIndex, "add_steps", stepIndex)}
                      style={{
                        background: "#ef4444",
                        color: "white",
                        border: "none",
                        borderRadius: "3px",
                        padding: "2px 6px",
                        fontSize: "0.6rem",
                        cursor: "pointer",
                      }}
                    >
                      削除
                    </button>
                  </div>
                  <StepEditor
                    step={addedStep}
                    localTestTargets={localTestTargets}
                    currentViewId={currentViewId}
                    mode="edit"
                    layout="compact"
                    stepIndex={undefined}
                    globalDropdownState={{ stepIndex: null, dropdownType: null }}
                    onGlobalDropdownChange={() => {}}
                    onUpdateStep={(field, value) => {
                      onUpdatePolicyStep(policyIndex, "add_steps", stepIndex, field as string, value);
                    }}
                    onUpdateAction={(action) => {
                      setEditedUseCase((prev) => {
                        if (!prev) return null;
                        const newPolicies = [...(prev.policies || [])];
                        const currentPolicy = newPolicies[policyIndex];
                        const currentStep = currentPolicy.changes.add_steps[stepIndex];

                        const defaultParams = getActionConfig(action)?.parameters.map((p) => ({
                          key: p.name,
                          value: p.type === "test_target" ? "" : "",
                        })) || [];

                        currentPolicy.changes.add_steps[stepIndex] = {
                          ...currentStep,
                          action_detail: {
                            ...currentStep.action_detail,
                            step_action: {
                              action: action as any,
                              parameters: defaultParams,
                            },
                            test_targets: currentStep.action_detail?.test_targets || [],
                          },
                        };

                        return { ...prev, policies: newPolicies };
                      });
                    }}
                    onUpdateParameter={(paramKey, value) => {
                      // パラメータ更新の実際の処理を定義
                      const updateParameter = (key: string, val: string) => {
                        setEditedUseCase((prev) => {
                          if (!prev) return null;
                          const newPolicies = [...(prev.policies || [])];
                          const currentPolicy = newPolicies[policyIndex];
                          const currentStep = currentPolicy.changes.add_steps[stepIndex];
                          const currentAction = currentStep.action_detail?.step_action;

                          if (currentAction) {
                            const paramConfig = getActionConfig(currentAction.action)?.parameters.find(
                              (p) => p.name === key
                            );

                            if (paramConfig?.type === "test_target") {
                              // test_targetパラメータの場合の処理
                              let updatedTestTargets = [...(currentStep.action_detail?.test_targets || [])];
                              
                              // パラメータごとに一意のIDを割り当てる
                              const actionConfig = getActionConfig(currentAction.action);
                              const testTargetParams = actionConfig?.parameters.filter(
                                (p) => p.type === "test_target"
                              ) || [];
                              const paramIndex = testTargetParams.findIndex(
                                (p) => p.name === key
                              );
                              const uniqueId = String(paramIndex + 1); // "1", "2", "3"...
                              
                              const existingParamValue = currentAction.parameters.find(p => p.key === key)?.value;
                              const existingEntry = updatedTestTargets.find(tt => tt.id === uniqueId);

                              if (val) {
                                // 新しい値が設定される場合
                                const targetName = localTestTargets.find((t) => t.id === val)?.name || "";
                                const newEntry = {
                                  id: uniqueId,
                                  test_target_id: val,
                                  name: targetName,
                                };

                                if (existingEntry) {
                                  // 既存のエントリを更新（他のエントリは保持）
                                  updatedTestTargets = updatedTestTargets.map(tt => 
                                    tt.id === uniqueId ? newEntry : tt
                                  );
                                } else {
                                  // 新しいエントリを追加（既存のエントリは保持）
                                  updatedTestTargets.push(newEntry);
                                }

                                // parametersを更新（test_targetsのIDを参照）
                                const newParameters = currentAction.parameters.map(p => 
                                  p.key === key ? { ...p, value: uniqueId } : p
                                );

                                currentPolicy.changes.add_steps[stepIndex] = {
                                  ...currentStep,
                                  action_detail: {
                                    ...currentStep.action_detail,
                                    step_action: {
                                      ...currentAction,
                                      parameters: newParameters,
                                    },
                                    test_targets: updatedTestTargets,
                                  },
                                };
                              } else {
                                // 値がクリアされる場合
                                // 該当するIDのエントリのみを削除（他のパラメータのエントリは保持）
                                updatedTestTargets = updatedTestTargets.filter(tt => tt.id !== uniqueId);

                                // parametersを更新（空文字を設定）
                                const newParameters = currentAction.parameters.map(p => 
                                  p.key === key ? { ...p, value: "" } : p
                                );

                                currentPolicy.changes.add_steps[stepIndex] = {
                                  ...currentStep,
                                  action_detail: {
                                    ...currentStep.action_detail,
                                    step_action: {
                                      ...currentAction,
                                      parameters: newParameters,
                                    },
                                    test_targets: updatedTestTargets,
                                  },
                                };
                              }
                            } else {
                              // 通常のパラメータの場合の処理
                              const newParameters = currentAction.parameters.map(p => 
                                p.key === key ? { ...p, value: val } : p
                              );

                              currentPolicy.changes.add_steps[stepIndex] = {
                                ...currentStep,
                                action_detail: {
                                  ...currentStep.action_detail,
                                  step_action: {
                                    ...currentAction,
                                    parameters: newParameters,
                                  },
                                  test_targets: currentStep.action_detail?.test_targets || [],
                                },
                              };
                            }
                          }

                          return { ...prev, policies: newPolicies };
                        });
                      };

                      // CREATE_NEWが選択された場合、新規テストターゲット作成モーダルを開く
                      if (value === "CREATE_NEW") {
                        // コールバック関数を作成
                        const handleNewTargetCreated = (newTarget: any) => {
                          // 新しく作成されたテストターゲットを自動選択
                          if (newTarget && newTarget.id) {
                            // 新規作成されたテストターゲットを直接使用してパラメータを更新
                            setEditedUseCase((prev) => {
                              if (!prev) return null;
                              const newPolicies = [...(prev.policies || [])];
                              const currentPolicy = newPolicies[policyIndex];
                              const currentStep = currentPolicy.changes.add_steps[stepIndex];
                              const currentAction = currentStep.action_detail?.step_action;

                              if (currentAction) {
                                const paramConfig = getActionConfig(currentAction.action)?.parameters.find(
                                  (p) => p.name === paramKey
                                );

                                if (paramConfig?.type === "test_target") {
                                  // test_targetパラメータの場合の処理
                                  // パラメータごとに一意のIDを割り当てる
                                  const actionConfig = getActionConfig(currentAction.action);
                                  const testTargetParams = actionConfig?.parameters.filter(
                                    (p) => p.type === "test_target"
                                  ) || [];
                                  const paramIndex = testTargetParams.findIndex(
                                    (p) => p.name === paramKey
                                  );
                                  const uniqueId = String(paramIndex + 1); // "1", "2", "3"...
                                  
                                  const newEntry = {
                                    id: uniqueId,
                                    test_target_id: newTarget.id,
                                    name: newTarget.name, // 新規作成されたテストターゲットの名前を直接使用
                                  };

                                  // 既存のtest_targetsから、同じIDのエントリを探す
                                  const currentTestTargets = currentStep.action_detail?.test_targets || [];
                                  const existingTargetIndex = currentTestTargets.findIndex(
                                    (tt) => tt.id === uniqueId
                                  );
                                  
                                  let updatedTestTargets: typeof currentTestTargets;
                                  if (existingTargetIndex >= 0) {
                                    // 既存のエントリを更新（他のエントリは保持）
                                    updatedTestTargets = [...currentTestTargets];
                                    updatedTestTargets[existingTargetIndex] = newEntry;
                                  } else {
                                    // 新しいエントリを追加（既存のエントリは保持）
                                    updatedTestTargets = [...currentTestTargets, newEntry];
                                  }

                                  // parametersを更新（test_targetsのIDを参照）
                                  const newParameters = currentAction.parameters.map(p => 
                                    p.key === paramKey ? { ...p, value: uniqueId } : p
                                  );

                                  currentPolicy.changes.add_steps[stepIndex] = {
                                    ...currentStep,
                                    action_detail: {
                                      ...currentStep.action_detail,
                                      step_action: {
                                        ...currentAction,
                                        parameters: newParameters,
                                      },
                                      test_targets: updatedTestTargets,
                                    },
                                  };
                                }
                              }

                              return { ...prev, policies: newPolicies };
                            });
                          }
                        };
                        
                        onCreateTestTarget?.(handleNewTargetCreated);
                        return;
                      }

                      // 通常の処理
                      updateParameter(paramKey, value);
                    }}
                    onCreateTestTarget={onCreateTestTarget}
                  />
                  {/* 必須パラメータバリデーションエラー表示 */}
                  {(() => {
                    const errorKey = `${policyIndex}-add_steps-${stepIndex}`;
                    return policyStepValidationErrors.has(errorKey) ? (
                      <div
                        style={{
                          padding: "8px 12px",
                          marginTop: "4px",
                          marginBottom: "4px",
                          backgroundColor: "#fee2e2",
                          border: "1px solid #fca5a5",
                          borderRadius: "4px",
                          color: "#991b1b",
                          fontSize: "0.75rem",
                        }}
                      >
                        必須パラメータが入力されていません
                      </div>
                    ) : null;
                  })()}
                </div>
              );
            }

            return (
              <div
                style={{
                  padding: "8px",
                  borderBottom: "1px solid #f3f4f6",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const button = e.currentTarget as HTMLButtonElement;
                    if (button.dataset.processing === "true") {
                      return;
                    }

                    button.dataset.processing = "true";

                    // 冪等性を確保するためにUUIDを事前生成
                    const newStepId = uuidv7();

                    setEditedUseCase((prev) => {
                      if (!prev) return null;
                      
                      // 重複防止: 同じstep_idがすでに存在する場合はスキップ
                      const newPolicies = [...(prev.policies || [])];
                      const currentPolicy = newPolicies[policyIndex];
                      
                      const existingStep = currentPolicy.changes.add_steps.find(
                        (s: any) => s.step_id === newStepId
                      );
                      if (existingStep) {
                        console.log("Step with same ID already exists, skipping");
                        return prev;
                      }

                      const defaultAction = "click" as const;
                      const actionConfig = getActionConfig(defaultAction);
                      const defaultParams: StepActionParameter[] =
                        actionConfig?.parameters.map((p) => ({
                          key: p.name,
                          value: p.type === "test_target" ? "" : "",
                        })) || [];

                      const newStep = {
                        step_id: newStepId,
                        step: "",
                        expect: "",
                        action_detail: {
                          step_action: {
                            action: defaultAction,
                            parameters: defaultParams,
                          },
                          test_targets: [],
                        },
                        insert_position: "start",
                      };

                      currentPolicy.changes.add_steps = [...currentPolicy.changes.add_steps, newStep];

                      return {
                        ...prev,
                        policies: newPolicies,
                      };
                    });

                    setTimeout(() => {
                      if (button) {
                        button.dataset.processing = "false";
                      }
                    }, 200);
                  }}
                  style={{
                    padding: "2px 8px",
                    fontSize: "0.7rem",
                    background: "#059669",
                    color: "white",
                    border: "none",
                    borderRadius: "3px",
                    cursor: "pointer",
                  }}
                >
                  + ここに追加
                </button>
              </div>
            );
          })()}

          {editedUseCase.steps.map((step: UseCaseStep, stepIdx: number) => {
            const isToDelete = policy.changes.remove_steps.some(
              (rs: any) => rs.step_id === step.step_id
            );
            const updateStep = policy.changes.update_steps.find(
              (us: any) => us.step_id === step.step_id
            );

            return (
              <React.Fragment key={`base-step-${stepIdx}-${step.step_id}`}>
                <div
                  style={{
                    padding: "12px",
                    borderBottom:
                      stepIdx < editedUseCase.steps.length - 1 ? "1px solid #f3f4f6" : "none",
                    background: isToDelete ? "#fef2f2" : updateStep ? "#fef3c7" : "white",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        minWidth: "24px",
                        height: "24px",
                        background: isToDelete ? "#ef4444" : updateStep ? "#f97316" : "#6b7280",
                        color: "white",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.7rem",
                        fontWeight: "600",
                        flexShrink: 0,
                      }}
                    >
                      {isToDelete ? "-" : updateStep ? "~" : stepIdx + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: "500",
                          color: "#1f2937",
                          marginBottom: "4px",
                          wordBreak: "break-word",
                        }}
                      >
                        ID: {step.step_id}
                      </div>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#4b5563",
                          marginBottom: "4px",
                          wordBreak: "break-word",
                        }}
                      >
                        {step.step}
                      </div>
                      {step.expect && (
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "#6b7280",
                            wordBreak: "break-word",
                          }}
                        >
                          期待: {step.expect}
                        </div>
                      )}

                      {/* 更新内容の表示・編集 */}
                      {updateStep && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "8px",
                            background: "#fed7aa",
                            borderRadius: "4px",
                            fontSize: "0.7rem",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "500",
                              color: "#ea580c",
                              marginBottom: "6px",
                            }}
                          >
                            変更後の内容:
                          </div>
                          <StepEditor
                            step={updateStep}
                            localTestTargets={localTestTargets}
                            currentViewId={currentViewId}
                            mode="edit"
                            layout="compact"
                            stepIndex={undefined}
                            globalDropdownState={{ stepIndex: null, dropdownType: null }}
                            onGlobalDropdownChange={() => {}}
                            onUpdateStep={(field, value) => {
                              const updateIndex = policy.changes.update_steps.findIndex(
                                (us: any) => us.step_id === step.step_id
                              );
                              if (updateIndex !== -1) {
                                onUpdatePolicyStep(
                                  policyIndex,
                                  "update_steps",
                                  updateIndex,
                                  field as string,
                                  value
                                );
                              }
                            }}
                            onUpdateAction={(action) => {
                              setEditedUseCase((prev) => {
                                if (!prev) return null;
                                const newPolicies = [...(prev.policies || [])];
                                const currentPolicy = newPolicies[policyIndex];
                                const updateIndex = currentPolicy.changes.update_steps.findIndex(
                                  (us: any) => us.step_id === step.step_id
                                );
                                if (updateIndex !== -1) {
                                  const currentStep = currentPolicy.changes.update_steps[updateIndex];
                                  const defaultParams = getActionConfig(action)?.parameters.map((p) => ({
                                    key: p.name,
                                    value: p.type === "test_target" ? "" : "",
                                  })) || [];

                                  currentPolicy.changes.update_steps[updateIndex] = {
                                    ...currentStep,
                                    action_detail: {
                                      ...currentStep.action_detail,
                                      step_action: {
                                        action: action as any,
                                        parameters: defaultParams,
                                      },
                                      test_targets: currentStep.action_detail?.test_targets || [],
                                    },
                                  };
                                }
                                return { ...prev, policies: newPolicies };
                              });
                            }}
                            onUpdateParameter={(paramKey, value) => {
                              setEditedUseCase((prev) => {
                                if (!prev) return null;
                                const newPolicies = [...(prev.policies || [])];
                                const currentPolicy = newPolicies[policyIndex];
                                const updateIndex = currentPolicy.changes.update_steps.findIndex(
                                  (us: any) => us.step_id === step.step_id
                                );
                                if (updateIndex !== -1) {
                                  const currentStep = currentPolicy.changes.update_steps[updateIndex];
                                  const currentAction = currentStep.action_detail?.step_action;
                                  
                                  if (currentAction) {
                                    const paramConfig = getActionConfig(currentAction.action)?.parameters.find(
                                      (p) => p.name === paramKey
                                    );
                                    
                                    if (paramConfig?.type === "test_target") {
                                      // test_targetパラメータの場合の処理
                                      let updatedTestTargets = [...(currentStep.action_detail?.test_targets || [])];
                                      
                                      // パラメータごとに一意のIDを割り当てる
                                      const actionConfig = getActionConfig(currentAction.action);
                                      const testTargetParams = actionConfig?.parameters.filter(
                                        (p) => p.type === "test_target"
                                      ) || [];
                                      const paramIndex = testTargetParams.findIndex(
                                        (p) => p.name === paramKey
                                      );
                                      const uniqueId = String(paramIndex + 1); // "1", "2", "3"...
                                      
                                      const existingEntry = updatedTestTargets.find(tt => tt.id === uniqueId);
                                      
                                      if (value) {
                                        // 新しい値が設定される場合
                                        const newEntry = {
                                          id: uniqueId,
                                          test_target_id: value,
                                          name: localTestTargets?.find(t => t.id === value)?.name || value
                                        };
                                        
                                        if (existingEntry) {
                                          // 既存のエントリを更新（他のエントリは保持）
                                          updatedTestTargets = updatedTestTargets.map(tt => 
                                            tt.id === uniqueId ? newEntry : tt
                                          );
                                        } else {
                                          // 新しいエントリを追加（既存のエントリは保持）
                                          updatedTestTargets.push(newEntry);
                                        }
                                      } else {
                                        // 値がクリアされる場合
                                        // 該当するIDのエントリのみを削除（他のパラメータのエントリは保持）
                                        updatedTestTargets = updatedTestTargets.filter(tt => tt.id !== uniqueId);
                                      }
                                      
                                      // parametersを更新
                                      const newParameters = currentAction.parameters.map(p => 
                                        p.key === paramKey ? { ...p, value: value ? uniqueId : "" } : p
                                      );
                                      
                                      currentPolicy.changes.update_steps[updateIndex] = {
                                        ...currentStep,
                                        action_detail: {
                                          ...currentStep.action_detail,
                                          step_action: {
                                            ...currentAction,
                                            parameters: newParameters,
                                          },
                                          test_targets: updatedTestTargets,
                                        },
                                      };
                                    } else {
                                      // 通常のパラメータの場合の処理
                                      const newParameters = currentAction.parameters.map(p => 
                                        p.key === paramKey ? { ...p, value } : p
                                      );
                                      
                                      currentPolicy.changes.update_steps[updateIndex] = {
                                        ...currentStep,
                                        action_detail: {
                                          ...currentStep.action_detail,
                                          step_action: {
                                            ...currentAction,
                                            parameters: newParameters,
                                          },
                                          test_targets: currentStep.action_detail?.test_targets || [],
                                        },
                                      };
                                    }
                                  }
                                }
                                return { ...prev, policies: newPolicies };
                              });
                            }}
                            onCreateTestTarget={onCreateTestTarget}
                          />
                          {/* 必須パラメータバリデーションエラー表示 */}
                          {(() => {
                            const updateIndex = policy.changes.update_steps.findIndex(
                              (us: any) => us.step_id === step.step_id
                            );
                            if (updateIndex !== -1) {
                              const errorKey = `${policyIndex}-update_steps-${updateIndex}`;
                              return policyStepValidationErrors.has(errorKey) ? (
                                <div
                                  style={{
                                    padding: "8px 12px",
                                    marginTop: "4px",
                                    marginBottom: "4px",
                                    backgroundColor: "#fee2e2",
                                    border: "1px solid #fca5a5",
                                    borderRadius: "4px",
                                    color: "#991b1b",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  必須パラメータが入力されていません
                                </div>
                              ) : null;
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>

                    {/* 右側のアクションボタン */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        alignItems: "flex-end",
                      }}
                    >
                      {!isToDelete && !updateStep && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              // 更新ステップを追加
                              setEditedUseCase((prev) => {
                                if (!prev) return null;
                                const newPolicies = [...(prev.policies || [])];
                                const currentPolicy = newPolicies[policyIndex];

                                const alreadyHasUpdate = currentPolicy.changes.update_steps.some(
                                  (us: any) => us.step_id === step.step_id
                                );

                                if (!alreadyHasUpdate) {
                                  const newUpdateStep = {
                                    step_id: step.step_id,
                                    step: step.step,
                                    expect: step.expect || "",
                                    action_detail: step.action_detail || {
                                      step_action: {
                                        action: "click",
                                        parameters: []
                                      },
                                      test_targets: []
                                    }
                                  };
                                  currentPolicy.changes.update_steps = [
                                    ...currentPolicy.changes.update_steps,
                                    newUpdateStep,
                                  ];
                                }

                                return { ...prev, policies: newPolicies };
                              });
                            }}
                            style={{
                              padding: "2px 6px",
                              fontSize: "0.6rem",
                              background: "#3b82f6",
                              color: "white",
                              border: "none",
                              borderRadius: "2px",
                              cursor: "pointer",
                            }}
                          >
                            更新
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              // 削除ステップを追加
                              setEditedUseCase((prev) => {
                                if (!prev) return null;
                                const newPolicies = [...(prev.policies || [])];
                                const currentPolicy = newPolicies[policyIndex];

                                const alreadyHasRemove = currentPolicy.changes.remove_steps.some(
                                  (rs: any) => rs.step_id === step.step_id
                                );

                                if (!alreadyHasRemove) {
                                  const newRemoveStep = {
                                    step_id: step.step_id,
                                  };
                                  currentPolicy.changes.remove_steps = [
                                    ...currentPolicy.changes.remove_steps,
                                    newRemoveStep,
                                  ];
                                }

                                return { ...prev, policies: newPolicies };
                              });
                            }}
                            style={{
                              padding: "2px 6px",
                              fontSize: "0.6rem",
                              background: "#ef4444",
                              color: "white",
                              border: "none",
                              borderRadius: "2px",
                              cursor: "pointer",
                            }}
                          >
                            削除
                          </button>
                        </>
                      )}

                      {updateStep && (
                        <button
                          type="button"
                          onClick={() => {
                            // 更新ステップを削除
                            setEditedUseCase((prev) => {
                              if (!prev) return null;
                              const newPolicies = [...(prev.policies || [])];
                              const currentPolicy = newPolicies[policyIndex];
                              currentPolicy.changes.update_steps =
                                currentPolicy.changes.update_steps.filter(
                                  (us: any) => us.step_id !== step.step_id
                                );
                              return { ...prev, policies: newPolicies };
                            });
                          }}
                          style={{
                            padding: "2px 6px",
                            fontSize: "0.6rem",
                            background: "#6b7280",
                            color: "white",
                            border: "none",
                            borderRadius: "2px",
                            cursor: "pointer",
                          }}
                        >
                          取消
                        </button>
                      )}

                      {isToDelete && (
                        <button
                          type="button"
                          onClick={() => {
                            // 削除ステップを取り消し
                            setEditedUseCase((prev) => {
                              if (!prev) return null;
                              const newPolicies = [...(prev.policies || [])];
                              const currentPolicy = newPolicies[policyIndex];
                              currentPolicy.changes.remove_steps =
                                currentPolicy.changes.remove_steps.filter(
                                  (rs: any) => rs.step_id !== step.step_id
                                );
                              return { ...prev, policies: newPolicies };
                            });
                          }}
                          style={{
                            padding: "2px 6px",
                            fontSize: "0.6rem",
                            background: "#6b7280",
                            color: "white",
                            border: "none",
                            borderRadius: "2px",
                            cursor: "pointer",
                          }}
                        >
                          取消
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* ステップの後に追加ボタンまたは入力フィールド */}
                {(() => {
                  const insertPosition = `after:${step.step_id}`;
                  const addedStep = policy.changes.add_steps.find(
                    (s: any) => s.insert_position === insertPosition
                  );

                  if (addedStep) {
                    const stepIndex = policy.changes.add_steps.findIndex(
                      (s: any) => s.insert_position === insertPosition
                    );
                    return (
                      <div
                        style={{
                          padding: "12px",
                          borderBottom: "1px solid #f3f4f6",
                          background: "#f0fdf4",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: "500",
                            color: "#059669",
                            marginBottom: "8px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span>+ ステップ {stepIdx + 1} の後に追加</span>
                          <button
                            type="button"
                            onClick={() => onRemovePolicyStep(policyIndex, "add_steps", stepIndex)}
                            style={{
                              background: "#ef4444",
                              color: "white",
                              border: "none",
                              borderRadius: "3px",
                              padding: "2px 6px",
                              fontSize: "0.6rem",
                              cursor: "pointer",
                            }}
                          >
                            削除
                          </button>
                        </div>
                        <StepEditor
                          step={addedStep}
                          localTestTargets={localTestTargets}
                          currentViewId={currentViewId}
                          mode="edit"
                          layout="compact"
                          stepIndex={undefined}
                          globalDropdownState={{ stepIndex: null, dropdownType: null }}
                          onGlobalDropdownChange={() => {}}
                          onUpdateStep={(field, value) => {
                            onUpdatePolicyStep(policyIndex, "add_steps", stepIndex, field as string, value);
                          }}
                          onUpdateAction={(action) => {
                            setEditedUseCase((prev) => {
                              if (!prev) return null;
                              const newPolicies = [...(prev.policies || [])];
                              const currentPolicy = newPolicies[policyIndex];
                              const currentStep = currentPolicy.changes.add_steps[stepIndex];

                              const defaultParams = getActionConfig(action)?.parameters.map((p) => ({
                                key: p.name,
                                value: p.type === "test_target" ? "" : "",
                              })) || [];

                              currentPolicy.changes.add_steps[stepIndex] = {
                                ...currentStep,
                                action_detail: {
                                  ...currentStep.action_detail,
                                  step_action: {
                                    action: action as any,
                                    parameters: defaultParams,
                                  },
                                  test_targets: currentStep.action_detail?.test_targets || [],
                                },
                              };

                              return { ...prev, policies: newPolicies };
                            });
                          }}
                          onUpdateParameter={(paramKey, value) => {
                            setEditedUseCase((prev) => {
                              if (!prev) return null;
                              const newPolicies = [...(prev.policies || [])];
                              const currentPolicy = newPolicies[policyIndex];
                              const currentStep = currentPolicy.changes.add_steps[stepIndex];
                              const currentAction = currentStep.action_detail?.step_action;

                              if (currentAction) {
                                const paramConfig = getActionConfig(currentAction.action)?.parameters.find(
                                  (p) => p.name === paramKey
                                );

                                if (paramConfig?.type === "test_target") {
                                  // test_targetパラメータの場合の処理
                                  let updatedTestTargets = [...(currentStep.action_detail?.test_targets || [])];
                                  
                                  // パラメータごとに一意のIDを割り当てる
                                  const actionConfig = getActionConfig(currentAction.action);
                                  const testTargetParams = actionConfig?.parameters.filter(
                                    (p) => p.type === "test_target"
                                  ) || [];
                                  const paramIndex = testTargetParams.findIndex(
                                    (p) => p.name === paramKey
                                  );
                                  const uniqueId = String(paramIndex + 1); // "1", "2", "3"...
                                  
                                  const existingEntry = updatedTestTargets.find(tt => tt.id === uniqueId);

                                  if (value) {
                                    // 新しい値が設定される場合
                                    const targetName = localTestTargets.find((t) => t.id === value)?.name || "";
                                    const newEntry = {
                                      id: uniqueId,
                                      test_target_id: value,
                                      name: targetName,
                                    };

                                    if (existingEntry) {
                                      // 既存のエントリを更新（他のエントリは保持）
                                      updatedTestTargets = updatedTestTargets.map(tt => 
                                        tt.id === uniqueId ? newEntry : tt
                                      );
                                    } else {
                                      // 新しいエントリを追加（既存のエントリは保持）
                                      updatedTestTargets.push(newEntry);
                                    }

                                    // parametersを更新（test_targetsのIDを参照）
                                    const newParameters = currentAction.parameters.map(p => 
                                      p.key === paramKey ? { ...p, value: uniqueId } : p
                                    );

                                    currentPolicy.changes.add_steps[stepIndex] = {
                                      ...currentStep,
                                      action_detail: {
                                        ...currentStep.action_detail,
                                        step_action: {
                                          ...currentAction,
                                          parameters: newParameters,
                                        },
                                        test_targets: updatedTestTargets,
                                      },
                                    };
                                  } else {
                                    // 値がクリアされる場合
                                    // 該当するIDのエントリのみを削除（他のパラメータのエントリは保持）
                                    updatedTestTargets = updatedTestTargets.filter(tt => tt.id !== uniqueId);

                                    // parametersを更新（空文字を設定）
                                    const newParameters = currentAction.parameters.map(p => 
                                      p.key === paramKey ? { ...p, value: "" } : p
                                    );

                                    currentPolicy.changes.add_steps[stepIndex] = {
                                      ...currentStep,
                                      action_detail: {
                                        ...currentStep.action_detail,
                                        step_action: {
                                          ...currentAction,
                                          parameters: newParameters,
                                        },
                                        test_targets: updatedTestTargets,
                                      },
                                    };
                                  }
                                } else {
                                  // 通常のパラメータの場合の処理
                                  const newParameters = currentAction.parameters.map(p => 
                                    p.key === paramKey ? { ...p, value } : p
                                  );

                                  currentPolicy.changes.add_steps[stepIndex] = {
                                    ...currentStep,
                                    action_detail: {
                                      ...currentStep.action_detail,
                                      step_action: {
                                        ...currentAction,
                                        parameters: newParameters,
                                      },
                                      test_targets: currentStep.action_detail?.test_targets || [],
                                    },
                                  };
                                }
                              }

                              return { ...prev, policies: newPolicies };
                            });
                          }}
                          onCreateTestTarget={onCreateTestTarget}
                        />
                        {/* 必須パラメータバリデーションエラー表示 */}
                        {(() => {
                          const errorKey = `${policyIndex}-add_steps-${stepIndex}`;
                          return policyStepValidationErrors.has(errorKey) ? (
                            <div
                              style={{
                                padding: "8px 12px",
                                marginTop: "4px",
                                marginBottom: "4px",
                                backgroundColor: "#fee2e2",
                                border: "1px solid #fca5a5",
                                borderRadius: "4px",
                                color: "#991b1b",
                                fontSize: "0.75rem",
                              }}
                            >
                              必須パラメータが入力されていません
                            </div>
                          ) : null;
                        })()}
                      </div>
                    );
                  }

                  return (
                    <div
                      style={{
                        padding: "8px",
                        borderBottom: "1px solid #f3f4f6",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          const button = e.currentTarget as HTMLButtonElement;
                          if (button.dataset.processing === "true") {
                            return;
                          }

                          button.dataset.processing = "true";

                          // 冪等性を確保するためにUUIDを事前生成
                          const newStepId = uuidv7();

                          setEditedUseCase((prev) => {
                            if (!prev) return null;
                            
                            // 重複防止: 同じstep_idがすでに存在する場合はスキップ
                            const newPolicies = [...(prev.policies || [])];
                            const currentPolicy = newPolicies[policyIndex];
                            
                            const existingStep = currentPolicy.changes.add_steps.find(
                              (s: any) => s.step_id === newStepId
                            );
                            if (existingStep) {
                              console.log("Step with same ID already exists, skipping");
                              return prev;
                            }

                            const defaultAction = "click" as const;
                            const actionConfig = getActionConfig(defaultAction);
                            const defaultParams: StepActionParameter[] =
                              actionConfig?.parameters.map((p) => ({
                                key: p.name,
                                value: p.type === "test_target" ? "" : "",
                              })) || [];

                            const newStep = {
                              step_id: newStepId,
                              step: "",
                              expect: "",
                              action_detail: {
                                step_action: {
                                  action: defaultAction,
                                  parameters: defaultParams,
                                },
                                test_targets: [],
                              },
                              insert_position: insertPosition,
                            };

                            currentPolicy.changes.add_steps = [
                              ...currentPolicy.changes.add_steps,
                              newStep,
                            ];

                            return {
                              ...prev,
                              policies: newPolicies,
                            };
                          });

                          setTimeout(() => {
                            if (button) {
                              button.dataset.processing = "false";
                            }
                          }, 200);
                        }}
                        style={{
                          padding: "2px 8px",
                          fontSize: "0.7rem",
                          background: "#059669",
                          color: "white",
                          border: "none",
                          borderRadius: "3px",
                          cursor: "pointer",
                        }}
                      >
                        + ここに追加
                      </button>
                    </div>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};